/* eslint-disable no-console */
import { getErrorReporter } from "@kaggle/components";
import { NOTEBOOK_EDITOR_JL_FRAME_RENDER_TID, ThemeName } from "@kaggle/core";
import { displaySnackbarMessage } from "@kaggle/material";
import { featureFlags, reportEvent } from "@kaggle/sdk";
import { EditorType } from "@kaggle/sdk/kernels";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import ServicesContext from "../../../api/ServicesContext";
import { getJupyterUrl } from "../../connector/extensions/utils/getJupyterUrl";
import { JupyterEvent } from "../../jupyter/types";
import { editorFrameClient as frameClient } from "../../pages/editor/editorFrameClient";
import { setJupyterStatus } from "../../redux/actions/jupyter";
import { reportLifecycleChange } from "../../redux/actions/lifecycle";
import { showFatalErrorModal } from "../../redux/actions/modal";
import { saveIpynb } from "../../redux/actions/notebook";
import { createSetSessionStateAction } from "../../redux/actions/session";
import { setConsoleExpanded } from "../../redux/actions/console";
import { isStopped, startWorker } from "../../redux/actions/worker";
import { observeSessionState } from "../../redux/observables/observeSessionState";
import {
  getCurrentEditorStatus,
  getCurrentIpynb,
} from "../../redux/selectors/current";
import { getJupyterProxyUrl } from "../../redux/selectors/jupyter";
import { getCurrentJupyterKernelName } from "../../redux/selectors/kernelDraft";
import { getCenteredLayoutModeEnabled } from "../../redux/selectors/layout";
import { getModalVisible } from "../../redux/selectors/modal";
import { getSessionState, getSourceType } from "../../redux/selectors/session";
import { AppDispatch, getStore } from "../../redux/store/appStore";
import { JupyterStatus } from "../../redux/types";
import { EditorStatus } from "../../types/EditorStatus";
import { LifecycleStage } from "../../types/LifecycleLatencyStats";

export const REDUX_ACTION_KEY = "REDUX_ACTION";
export const SAVE_IPYNB_ACTION_KEY = "SAVEIPYNB_ACTION";
export const EDITOR_READY_ACTION_KEY = "EDITOR_READY";
export const EDITOR_INIT_FINISHED_ACTION_KEY = "EDITOR_INIT_FINISHED";

/**
 * The id of the next element to receive focus, when tab is pressed while the
 * editor frame container is currently focused. See: b/267463410
 */
export const FRAME_CONTAINER_ELEMENT_ID = "frame-container-element";
/**
 * Special HTMLElement id for directing tab focus path after the frame
 * container is selected. We need to bypass the frame's contents to allow for
 * natural tab focus path across the editor's outer page
 */
export const FRAME_CONTAINER_NEXT_FOCUS_ELEMENT_ID =
  "frame-container-next-focus-element";
/**
 * Cross-frame message key that gets emitted by the juptyer frame, indicating
 * that we should shift focus the frame container.
 */
export const FOCUS_FRAME_CONTAINER_ACTION_KEY =
  "FOCUS_FRAME_CONTAINER_ACTION_KEY";

const FrameContainer = styled.div<{
  $isModalVisible: boolean;
  $centeredLayoutMode: boolean;
}>`
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;

  ${({ $centeredLayoutMode, theme }) =>
    $centeredLayoutMode && theme.km.spacing.withContentBody}

  iframe {
    border: 0;
    flex: 1;

    ${p => p.$isModalVisible && "pointer-events: none"};
  }
`;

export const SessionlessEditor = () => {
  const { useEffect } = React;
  const services = React.useContext(ServicesContext);
  const dispatch: AppDispatch = useDispatch();
  const store = getStore();
  const frameContainerRef = React.useRef<HTMLDivElement>(null);
  const session = useSelector(getSessionState);
  const baseJupyterUrl = getJupyterUrl();
  const proxyUrl = useSelector(getJupyterProxyUrl);
  const kernelName = useSelector(getCurrentJupyterKernelName);
  const editorStatus = useSelector(getCurrentEditorStatus);
  const ipynb = useSelector(getCurrentIpynb);
  const isModalVisible = useSelector(getModalVisible);
  const centeredLayoutMode = useSelector(getCenteredLayoutModeEnabled);
  const initStarted = React.useRef<boolean>(false);

  const [frameContainerFocused, setFrameContainerFocused] =
    React.useState(false);

  useEffect(() => {
    if (!ipynb || initStarted.current) {
      return;
    }

    initStarted.current = true;

    if (!frameContainerRef.current) {
      throw new Error("No reference to the current editor frame container.");
    }

    if (!baseJupyterUrl) {
      throw new Error("Jupyter URL was not set.");
    }

    const isSessionStarting = !store.getState().notebook.startedCanceled;
    if (isSessionStarting) {
      displaySnackbarMessage({ title: "Session is starting..." });
    }

    // Conditionally passes a querystring parameter that will
    // un-disable the LSP autocomplete extension (b/211487924).
    //
    // NOTE: this will still be required even after LSP launches
    // since there will always be some kernels on older docker
    // containers that don't support LSP. In those cases, the iframe
    // should not enable LSP in order to allow Jupyter to fall back
    // to non-LSP autocomplete.
    const jupyterUrl = store.getState().enableLsp
      ? baseJupyterUrl + "&lsp=true"
      : baseJupyterUrl;

    const frameContainer = frameContainerRef.current;
    services.frameClient
      .createJupyterFrame(async () => {
        await frameClient.connect(frameContainer, jupyterUrl);

        frameClient.setContent(ipynb);

        let lastThemeName: ThemeName;
        let lastSourceType: EditorType;
        observeSessionState(store, ({ themeName, sourceType }) => {
          if (lastThemeName !== themeName) {
            frameClient.setTheme(themeName);
            lastThemeName = themeName;
          }
          if (lastSourceType !== sourceType) {
            frameClient.setSourceType(sourceType);
            lastSourceType = sourceType;
          }
        });

        frameClient.on("errorReport", (e: Error) => {
          getErrorReporter()?.report(e);
        });

        frameClient.on(EDITOR_READY_ACTION_KEY, () => {
          dispatch(setJupyterStatus(JupyterStatus.RUNNING));

          dispatch(
            reportLifecycleChange(
              LifecycleStage.EDITOR_FRAME_COMMUNICATION_ESTABLISHED,
              Date.now()
            )
          );

          // Immediately move to disconnected if the notebook is actually offline.
          // We need to move to RUNNING before this so that timeToEdit metrics
          // still properly report how long it took to load the editor.
          const disconnected = store.getState().notebook.startedCanceled;
          if (disconnected) {
            dispatch(setJupyterStatus(JupyterStatus.DISCONNECTED));
          }
        });

        frameClient.on(EDITOR_INIT_FINISHED_ACTION_KEY, () => {
          // Add a test attribute for e2e browser tests after bootstrap init
          // has completed.
          frameContainer.setAttribute(
            "data-testid",
            NOTEBOOK_EDITOR_JL_FRAME_RENDER_TID
          );
          frameContainer.setAttribute("title", "Notebook editor cells");
        });

        // Events emitted by the child to be handled by the parent.
        frameClient.on(REDUX_ACTION_KEY, dispatch);

        frameClient.on(SAVE_IPYNB_ACTION_KEY, async () => {
          await dispatch(
            saveIpynb(() => {
              if (featureFlags.kernelsSaveCellOutput) {
                return frameClient.getRAWSourceWithOutputs();
              } else {
                return frameClient.getRAWSource();
              }
            })
          );
        });

        frameClient.on(JupyterEvent.CELL_DELETE, () => {
          displaySnackbarMessage({
            title: "Cell deleted",
            actions: [
              {
                label: "Undo",
                onClick: () => {
                  reportEvent("kernels", "editor-cell-deleted-snackbar-undo");
                  frameClient.undoCellDelete();
                  // Because we can only undo the last deleted cell, there's
                  // not really a point to let users undo multiple times.
                },
              },
              {
                icon: "close",
                onClick: () => {
                  reportEvent(
                    "kernels",
                    "editor-cell-deleted-snackbar-dismiss"
                  );
                },
              },
            ],
          });
        });

        frameClient.on(JupyterEvent.CODE_CELL_EXECUTE, () => {
          if (isStopped(store.getState())) {
            // Automatically start worker when stopped on cell execute.
            store.dispatch(startWorker());
          }

          const sourceType = getSourceType(store.getState());
          if (sourceType === EditorType.SCRIPT) {
            dispatch(setConsoleExpanded(true));
          }
        });

        frameClient.dispatch(createSetSessionStateAction(session));

        return frameClient.frame;
      })
      .then(() => frameClient.initialize());
  }, [dispatch, ipynb]);

  useEffect(() => {
    frameClient.dispatch(createSetSessionStateAction(session));
  }, [session]);

  useEffect(() => {
    if (!frameContainerRef.current) {
      throw new Error("No reference to the current editor frame container.");
    }

    frameContainerRef.current.style.display =
      editorStatus === EditorStatus.QUEUED ? "none" : "flex";

    switch (editorStatus) {
      case EditorStatus.FATAL:
        frameClient.lockEditor();
        dispatch(showFatalErrorModal());
        break;
    }
  }, [dispatch, editorStatus]);

  useEffect(() => {
    if (!proxyUrl) {
      return;
    }
    if (!kernelName) {
      throw new Error("Kernel name was not set.");
    }
    frameClient.connectKernel(proxyUrl, kernelName);
  }, [proxyUrl]);

  useEffect(() => {
    frameClient.on(FOCUS_FRAME_CONTAINER_ACTION_KEY, () => {
      if (frameContainerRef.current) {
        frameContainerRef.current.focus();
      }
    });
  }, []);

  return (
    <FrameContainer
      ref={frameContainerRef}
      $isModalVisible={isModalVisible}
      $centeredLayoutMode={centeredLayoutMode}
      id={FRAME_CONTAINER_ELEMENT_ID}
      onKeyDown={evt => {
        if (frameContainerFocused) {
          // Don't intercept shift + tab, we want that to behave normally.
          if (!evt.shiftKey && evt.code === "Tab") {
            // We're intercepting the default to redirect it elsewhere.
            evt.preventDefault();
            document
              .getElementById(FRAME_CONTAINER_NEXT_FOCUS_ELEMENT_ID)
              ?.focus();
          }
          // Pressing enter will transfer focus into the editor frame.
          if (evt.code === "Enter") {
            frameClient.focusActiveCell();
          }
        }
      }}
      onFocus={() => {
        setFrameContainerFocused(true);
      }}
      onBlur={() => {
        setFrameContainerFocused(false);
      }}
      // We need the frame container to be focusable for our a11y solution.
      tabIndex={0}
    />
  );
};
